import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Package, 
  CreditCard, 
  Truck, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  Search,
  Eye,
  RefreshCw,
  X,
  Send,
  FileText,
  Calculator,
  Phone,
  Mail,
  Building2,
  User,
  MessageSquare,
  ShieldAlert,
  LogIn,
  Lock,
  Boxes,
  AlertTriangle,
  Edit2,
  Save,
  Upload,
  Image as ImageIcon,
  Trash2,
  HelpCircle,
  Plus,
  ChevronDown,
  ChevronUp,
  GripVertical,
  Images
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import ProductImageManager from '../components/ProductImageManager';
import ManualInvoiceForm from '../components/ManualInvoiceForm';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const ADMIN_KEY = 'jnm-admin-2024';

// Access Denied Component
const AccessDenied = ({ isAuthenticated, isLoading }) => {
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-12 w-12 animate-spin mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600">Lade...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            {isAuthenticated ? (
              <ShieldAlert className="h-8 w-8 text-red-600" />
            ) : (
              <Lock className="h-8 w-8 text-red-600" />
            )}
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {isAuthenticated ? 'Zugriff verweigert' : 'Anmeldung erforderlich'}
          </h1>
          
          <p className="text-gray-600 mb-6">
            {isAuthenticated 
              ? 'Sie haben keine Berechtigung, auf das Admin-Dashboard zuzugreifen. Kontaktieren Sie den Administrator, wenn Sie Zugriff benötigen.'
              : 'Bitte melden Sie sich mit einem Admin-Konto an, um auf das Dashboard zuzugreifen.'}
          </p>
          
          {!isAuthenticated ? (
            <div className="space-y-3">
              <Link to="/anmelden">
                <Button className="w-full bg-red-600 hover:bg-red-700">
                  <LogIn className="mr-2 h-4 w-4" />
                  Mit E-Mail anmelden
                </Button>
              </Link>
            </div>
          ) : (
            <Link to="/">
              <Button className="bg-red-600 hover:bg-red-700">
                Zurück zur Startseite
              </Button>
            </Link>
          )}
          
          <div className="mt-6 pt-6 border-t">
            <p className="text-sm text-gray-500">
              Admin-Bereich für autorisierte Mitarbeiter von Jannesmann Baumaschinen
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const AdminDashboard = () => {
  const { user, loading: authLoading, isAdmin, isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [financingRequests, setFinancingRequests] = useState([]);
  const [financingStats, setFinancingStats] = useState(null);
  const [inventory, setInventory] = useState({ products: [], accessories: [], stats: {} });
  const [inventoryStats, setInventoryStats] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedFinancing, setSelectedFinancing] = useState(null);
  const [filter, setFilter] = useState({ status: '', payment: '', method: '', preorder: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [editingStock, setEditingStock] = useState(null);
  const [newStockValue, setNewStockValue] = useState('');
  const [editingPrice, setEditingPrice] = useState(null);
  const [newPriceValue, setNewPriceValue] = useState('');
  // New states for product details editing
  const [editingProduct, setEditingProduct] = useState(null);
  const [productEditForm, setProductEditForm] = useState({ name: '', model: '', deliveryTime: '', availabilityStatus: '' });
  // Image upload states
  const [uploadingImage, setUploadingImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [editingImageUrl, setEditingImageUrl] = useState(null); // { slug, type, url }
  const fileInputRef = useRef(null);
  
  // FAQ states
  const [faqCategories, setFaqCategories] = useState([]);
  const [faqLoading, setFaqLoading] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategory, setNewCategory] = useState({ title: '', icon: '❓', questions: [] });
  const [showNewCategoryForm, setShowNewCategoryForm] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});

  // Fetch orders and stats - MUST be defined before conditional returns (React Hooks rules)
  const fetchData = useCallback(async () => {
    // Skip if not admin
    if (!isAuthenticated || !isAdmin) return;
    
    try {
      setLoading(true);
      
      const params = new URLSearchParams();
      if (filter.status) params.append('status', filter.status);
      if (filter.payment) params.append('payment_status', filter.payment);
      if (filter.method) params.append('payment_method', filter.method);
      
      const [ordersRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/orders?${params}`, {
          headers: { 'X-Admin-Key': ADMIN_KEY }
        }),
        fetch(`${API_URL}/api/admin/stats`, {
          headers: { 'X-Admin-Key': ADMIN_KEY }
        })
      ]);
      
      if (!ordersRes.ok || !statsRes.ok) {
        throw new Error('Fehler beim Laden der Daten');
      }
      
      const ordersData = await ordersRes.json();
      const statsData = await statsRes.json();
      
      setOrders(ordersData.orders || []);
      setStats(statsData);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Fehler beim Laden der Bestellungen');
    } finally {
      setLoading(false);
    }
  }, [filter, isAuthenticated, isAdmin]);

  // Fetch financing requests - MUST be defined before conditional returns (React Hooks rules)
  const fetchFinancingRequests = useCallback(async () => {
    // Skip if not admin
    if (!isAuthenticated || !isAdmin) return;
    
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/financing/requests`, {
        headers: { 'X-Admin-Key': ADMIN_KEY }
      });
      
      if (!res.ok) throw new Error('Fehler beim Laden');
      
      const data = await res.json();
      setFinancingRequests(data.requests || []);
      setFinancingStats(data.stats || null);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Fehler beim Laden der Finanzierungsanfragen');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, isAdmin]);

  // Fetch inventory data
  const fetchInventory = useCallback(async () => {
    if (!isAuthenticated || !isAdmin) return;
    
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/admin/inventory`, {
        headers: { 'X-Admin-Key': ADMIN_KEY }
      });
      
      if (!res.ok) throw new Error('Fehler beim Laden');
      
      const data = await res.json();
      setInventory(data);
      setInventoryStats(data.stats || null);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Fehler beim Laden des Inventars');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, isAdmin]);

  // Fetch FAQ categories
  const fetchFaqCategories = useCallback(async () => {
    if (!isAuthenticated || !isAdmin) return;
    
    try {
      setFaqLoading(true);
      const res = await fetch(`${API_URL}/api/faq/categories`);
      
      if (!res.ok) throw new Error('Fehler beim Laden');
      
      const data = await res.json();
      setFaqCategories(data.categories || []);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Fehler beim Laden der FAQ-Kategorien');
    } finally {
      setFaqLoading(false);
    }
  }, [isAuthenticated, isAdmin]);

  useEffect(() => {
    // Skip if not admin
    if (!isAuthenticated || !isAdmin) return;
    
    if (activeTab === 'orders') {
      fetchData();
    } else if (activeTab === 'financing') {
      fetchFinancingRequests();
    } else if (activeTab === 'inventory') {
      fetchInventory();
    } else if (activeTab === 'faq') {
      fetchFaqCategories();
    }
  }, [activeTab, fetchData, fetchFinancingRequests, fetchInventory, fetchFaqCategories, isAuthenticated, isAdmin]);

  // Check admin access - AFTER all hooks are defined
  if (authLoading) {
    return <AccessDenied isAuthenticated={false} isLoading={true} />;
  }
  
  if (!isAuthenticated || !isAdmin) {
    return <AccessDenied isAuthenticated={isAuthenticated} isLoading={false} />;
  }

  // Update order status
  const updateOrderStatus = async (orderId, status) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/orders/${orderId}/status`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json',
          'X-Admin-Key': ADMIN_KEY 
        },
        body: JSON.stringify({ status })
      });
      
      if (!res.ok) throw new Error('Fehler beim Aktualisieren');
      
      toast.success(`Status auf "${status}" geändert`);
      fetchData();
      if (selectedOrder?.id === orderId) {
        setSelectedOrder(prev => ({ ...prev, order_status: status }));
      }
    } catch (error) {
      toast.error('Fehler beim Aktualisieren des Status');
    }
  };

  // Confirm payment
  const confirmPayment = async (orderId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/orders/${orderId}/payment`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json',
          'X-Admin-Key': ADMIN_KEY 
        },
        body: JSON.stringify({ payment_status: 'paid' })
      });
      
      if (!res.ok) throw new Error('Fehler beim Bestätigen');
      
      toast.success('Zahlung bestätigt! E-Mail wird gesendet.');
      fetchData();
      if (selectedOrder?.id === orderId) {
        setSelectedOrder(prev => ({ ...prev, payment_status: 'paid' }));
      }
    } catch (error) {
      toast.error('Fehler beim Bestätigen der Zahlung');
    }
  };

  // Add tracking
  const addTracking = async (orderId, trackingNumber, carrier) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/orders/${orderId}/tracking`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json',
          'X-Admin-Key': ADMIN_KEY 
        },
        body: JSON.stringify({ tracking_number: trackingNumber, tracking_carrier: carrier })
      });
      
      if (!res.ok) throw new Error('Fehler beim Hinzufügen');
      
      toast.success('Tracking hinzugefügt! Versandbenachrichtigung wird gesendet.');
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Hinzufügen des Trackings');
    }
  };

  // Update financing status
  const updateFinancingStatus = async (requestId, status) => {
    try {
      const res = await fetch(`${API_URL}/api/financing/requests/${requestId}/status?status=${status}`, {
        method: 'PATCH',
        headers: { 'X-Admin-Key': ADMIN_KEY }
      });
      
      if (!res.ok) throw new Error('Fehler beim Aktualisieren');
      
      toast.success(`Status auf "${status}" geändert`);
      fetchFinancingRequests();
      if (selectedFinancing?.id === requestId) {
        setSelectedFinancing(prev => ({ ...prev, status }));
      }
    } catch (error) {
      toast.error('Fehler beim Aktualisieren des Status');
    }
  };

  // Delete order permanently
  const deleteOrderPermanently = async (orderId) => {
    if (!window.confirm('Bestellung DAUERHAFT löschen? Diese Aktion kann nicht rückgängig gemacht werden!')) {
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/admin/orders/${orderId}?permanent=true`, {
        method: 'DELETE',
        headers: { 'X-Admin-Key': ADMIN_KEY }
      });
      
      if (!res.ok) throw new Error('Fehler beim Löschen');
      
      toast.success('Bestellung dauerhaft gelöscht');
      setSelectedOrder(null);
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Löschen der Bestellung');
    }
  };

  // Delete all orders
  const deleteAllOrders = async () => {
    const confirmText = prompt('Um ALLE Bestellungen zu löschen, geben Sie "ALLE LÖSCHEN" ein:');
    if (confirmText !== 'ALLE LÖSCHEN') {
      toast.info('Löschvorgang abgebrochen');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/admin/orders/bulk/delete-all`, {
        method: 'DELETE',
        headers: { 'X-Admin-Key': ADMIN_KEY }
      });
      
      if (!res.ok) throw new Error('Fehler beim Löschen');
      
      const data = await res.json();
      toast.success(data.message);
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Löschen aller Bestellungen');
    }
  };

  // Delete financing request
  const deleteFinancingRequest = async (requestId) => {
    if (!window.confirm('Finanzierungsanfrage dauerhaft löschen?')) {
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/admin/financing/${requestId}`, {
        method: 'DELETE',
        headers: { 'X-Admin-Key': ADMIN_KEY }
      });
      
      if (!res.ok) throw new Error('Fehler beim Löschen');
      
      toast.success('Finanzierungsanfrage gelöscht');
      setSelectedFinancing(null);
      fetchFinancingRequests();
    } catch (error) {
      toast.error('Fehler beim Löschen der Finanzierungsanfrage');
    }
  };

  // Delete all financing requests
  const deleteAllFinancingRequests = async () => {
    const confirmText = prompt('Um ALLE Finanzierungsanfragen zu löschen, geben Sie "ALLE LÖSCHEN" ein:');
    if (confirmText !== 'ALLE LÖSCHEN') {
      toast.info('Löschvorgang abgebrochen');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/admin/financing/bulk/delete-all`, {
        method: 'DELETE',
        headers: { 'X-Admin-Key': ADMIN_KEY }
      });
      
      if (!res.ok) throw new Error('Fehler beim Löschen');
      
      const data = await res.json();
      toast.success(data.message);
      fetchFinancingRequests();
    } catch (error) {
      toast.error('Fehler beim Löschen aller Finanzierungsanfragen');
    }
  };

  // Update stock quantity
  const updateStock = async (slug, type, quantity) => {
    try {
      const endpoint = type === 'product' ? 'products' : 'accessories';
      const res = await fetch(`${API_URL}/api/admin/${endpoint}/${slug}/stock?quantity=${quantity}`, {
        method: 'PATCH',
        headers: { 'X-Admin-Key': ADMIN_KEY }
      });
      
      if (!res.ok) throw new Error('Fehler beim Aktualisieren');
      
      toast.success(`Bestand auf ${quantity} aktualisiert`);
      setEditingStock(null);
      setNewStockValue('');
      fetchInventory();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren des Bestands');
    }
  };

  // Update price
  const updatePrice = async (slug, type, netPrice) => {
    try {
      const endpoint = type === 'product' ? 'products' : 'accessories';
      const res = await fetch(`${API_URL}/api/admin/${endpoint}/${slug}/price?net_price=${netPrice}`, {
        method: 'PATCH',
        headers: { 'X-Admin-Key': ADMIN_KEY }
      });
      
      if (!res.ok) throw new Error('Fehler beim Aktualisieren');
      
      const data = await res.json();
      toast.success(`Preis aktualisiert: ${formatPrice(netPrice)} (netto) / ${formatPrice(data.grossPrice)} (brutto)`);
      setEditingPrice(null);
      setNewPriceValue('');
      fetchInventory();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren des Preises');
    }
  };

  // Update product details (name, model, deliveryTime)
  const updateProductDetails = async (slug, type, details) => {
    try {
      const endpoint = type === 'product' ? 'products' : 'accessories';
      const params = new URLSearchParams();
      if (details.name) params.append('name', details.name);
      if (details.model) params.append('model', details.model);
      if (details.deliveryTime) params.append('deliveryTime', details.deliveryTime);
      if (details.availabilityStatus) params.append('availabilityStatus', details.availabilityStatus);
      
      const res = await fetch(`${API_URL}/api/admin/${endpoint}/${slug}/details?${params}`, {
        method: 'PATCH',
        headers: { 'X-Admin-Key': ADMIN_KEY }
      });
      
      if (!res.ok) throw new Error('Fehler beim Aktualisieren');
      
      toast.success('Produktdetails aktualisiert');
      setEditingProduct(null);
      setProductEditForm({ name: '', model: '', deliveryTime: '', availabilityStatus: '' });
      fetchInventory();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren der Produktdetails');
    }
  };

  // Image upload function
  const handleImageUpload = async (file, slug, type) => {
    if (!file) return;
    
    try {
      setUploadingImage(`${type}-${slug}`);
      
      // Create form data
      const formData = new FormData();
      formData.append('file', file);
      
      // Upload file
      const uploadRes = await fetch(`${API_URL}/api/admin/upload-image`, {
        method: 'POST',
        headers: { 'X-Admin-Key': ADMIN_KEY },
        body: formData
      });
      
      if (!uploadRes.ok) {
        const err = await uploadRes.json();
        throw new Error(err.detail || 'Upload fehlgeschlagen');
      }
      
      const uploadData = await uploadRes.json();
      
      // Update product/accessory with new image URL
      const endpoint = type === 'product' ? 'products' : 'accessories';
      const updateRes = await fetch(`${API_URL}/api/admin/${endpoint}/${slug}/image?image_url=${encodeURIComponent(uploadData.url)}`, {
        method: 'PATCH',
        headers: { 'X-Admin-Key': ADMIN_KEY }
      });
      
      if (!updateRes.ok) throw new Error('Bild konnte nicht gespeichert werden');
      
      toast.success('Bild erfolgreich hochgeladen');
      setImagePreview(null);
      fetchInventory();
    } catch (error) {
      toast.error(error.message || 'Fehler beim Hochladen');
    } finally {
      setUploadingImage(null);
    }
  };

  // Update image via external URL
  const handleImageUrlUpdate = async (slug, type, imageUrl) => {
    if (!imageUrl || !imageUrl.startsWith('http')) {
      toast.error('Bitte eine gültige URL eingeben (https://...)');
      return;
    }
    
    try {
      const endpoint = type === 'product' ? 'products' : 'accessories';
      const res = await fetch(`${API_URL}/api/admin/${endpoint}/${slug}/image?image_url=${encodeURIComponent(imageUrl)}`, {
        method: 'PATCH',
        headers: { 'X-Admin-Key': ADMIN_KEY }
      });
      
      if (!res.ok) throw new Error('Bild konnte nicht aktualisiert werden');
      
      toast.success('Bild-URL erfolgreich aktualisiert');
      fetchInventory();
    } catch (error) {
      toast.error(error.message || 'Fehler beim Aktualisieren');
    }
  };

  // Delete/Clear image
  const handleImageDelete = async (slug, type) => {
    try {
      const endpoint = type === 'product' ? 'products' : 'accessories';
      const res = await fetch(`${API_URL}/api/admin/${endpoint}/${slug}/image?image_url=`, {
        method: 'PATCH',
        headers: { 'X-Admin-Key': ADMIN_KEY }
      });
      
      if (!res.ok) throw new Error('Bild konnte nicht gelöscht werden');
      
      toast.success('Bild erfolgreich entfernt');
      fetchInventory();
    } catch (error) {
      toast.error(error.message || 'Fehler beim Löschen');
    }
  };

  // Handle file input change
  const handleFileSelect = (e, slug, type) => {
    const file = e.target.files[0];
    if (file) {
      // Show preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview({ slug, type, url: reader.result, file });
      };
      reader.readAsDataURL(file);
    }
  };

  // Initialize stock for all products
  const initializeStock = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/inventory/initialize`, {
        method: 'POST',
        headers: { 'X-Admin-Key': ADMIN_KEY }
      });
      
      if (!res.ok) throw new Error('Fehler');
      
      const data = await res.json();
      toast.success(data.message);
      fetchInventory();
    } catch (error) {
      toast.error('Fehler bei der Initialisierung');
    }
  };

  // Format price
  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(price || 0);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      processing: 'bg-purple-100 text-purple-800',
      shipped: 'bg-indigo-100 text-indigo-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    const labels = {
      pending: 'Ausstehend',
      confirmed: 'Bestätigt',
      processing: 'In Bearbeitung',
      shipped: 'Versendet',
      delivered: 'Zugestellt',
      cancelled: 'Storniert'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100'}`}>
        {labels[status] || status}
      </span>
    );
  };

  // Get payment badge
  const getPaymentBadge = (status) => {
    const styles = {
      pending: 'bg-gray-100 text-gray-800',
      awaiting_payment: 'bg-orange-100 text-orange-800',
      paid: 'bg-green-100 text-green-800',
      refunded: 'bg-red-100 text-red-800'
    };
    const labels = {
      pending: 'Ausstehend',
      awaiting_payment: 'Zahlung offen',
      paid: 'Bezahlt',
      refunded: 'Erstattet'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100'}`}>
        {labels[status] || status}
      </span>
    );
  };

  // Get financing status badge
  const getFinancingStatusBadge = (status) => {
    const styles = {
      neu: 'bg-blue-100 text-blue-800',
      in_bearbeitung: 'bg-yellow-100 text-yellow-800',
      angebot_erstellt: 'bg-purple-100 text-purple-800',
      abgeschlossen: 'bg-green-100 text-green-800',
      abgelehnt: 'bg-red-100 text-red-800'
    };
    const labels = {
      neu: 'Neu',
      in_bearbeitung: 'In Bearbeitung',
      angebot_erstellt: 'Angebot erstellt',
      abgeschlossen: 'Abgeschlossen',
      abgelehnt: 'Abgelehnt'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100'}`}>
        {labels[status] || status}
      </span>
    );
  };

  // Get preorder status badge (special statuses for preorders)
  const getPreorderStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-800',
      vorbestellt: 'bg-amber-100 text-amber-800',
      reserviert: 'bg-blue-100 text-blue-800',
      lieferbereit: 'bg-purple-100 text-purple-800',
      abgeschlossen: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    const labels = {
      pending: 'Vorbestellt',
      vorbestellt: 'Vorbestellt',
      reserviert: 'Reserviert',
      lieferbereit: 'Lieferbereit',
      abgeschlossen: 'Abgeschlossen',
      cancelled: 'Storniert'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-amber-100 text-amber-800'}`}>
        {labels[status] || 'Vorbestellt'}
      </span>
    );
  };

  // Filter orders by search and preorder status
  const filteredOrders = orders.filter(order => {
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesSearch = (
        order.order_number?.toLowerCase().includes(search) ||
        order.customer_name?.toLowerCase().includes(search) ||
        order.customer_email?.toLowerCase().includes(search)
      );
      if (!matchesSearch) return false;
    }
    
    // Preorder filter
    if (filter.preorder === 'preorder' && !order.is_preorder) return false;
    if (filter.preorder === 'regular' && order.is_preorder) return false;
    
    return true;
  });

  // Filter financing requests by search
  const filteredFinancing = financingRequests.filter(req => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      req.first_name?.toLowerCase().includes(search) ||
      req.last_name?.toLowerCase().includes(search) ||
      req.email?.toLowerCase().includes(search) ||
      req.company?.toLowerCase().includes(search) ||
      req.product_name?.toLowerCase().includes(search)
    );
  });

  // FAQ Management Functions
  const seedFaqData = async () => {
    try {
      setFaqLoading(true);
      const res = await fetch(`${API_URL}/api/faq/seed`, {
        method: 'POST',
        headers: { 'X-Admin-Key': ADMIN_KEY }
      });
      
      if (!res.ok) throw new Error('Fehler beim Anlegen');
      
      const data = await res.json();
      toast.success(data.message);
      fetchFaqCategories();
    } catch (error) {
      toast.error('Fehler beim Anlegen der FAQ-Daten');
    } finally {
      setFaqLoading(false);
    }
  };

  const saveFaqCategory = async (category) => {
    try {
      setFaqLoading(true);
      const method = category.id && faqCategories.find(c => c.id === category.id) ? 'PUT' : 'POST';
      const url = method === 'PUT' 
        ? `${API_URL}/api/faq/categories/${category.id}`
        : `${API_URL}/api/faq/categories`;
      
      const res = await fetch(url, {
        method,
        headers: { 
          'Content-Type': 'application/json',
          'X-Admin-Key': ADMIN_KEY 
        },
        body: JSON.stringify(category)
      });
      
      if (!res.ok) throw new Error('Fehler beim Speichern');
      
      toast.success('Kategorie gespeichert');
      setEditingCategory(null);
      setShowNewCategoryForm(false);
      setNewCategory({ title: '', icon: '❓', questions: [] });
      fetchFaqCategories();
    } catch (error) {
      toast.error('Fehler beim Speichern der Kategorie');
    } finally {
      setFaqLoading(false);
    }
  };

  const deleteFaqCategory = async (categoryId) => {
    if (!window.confirm('Möchten Sie diese Kategorie wirklich löschen?')) return;
    
    try {
      setFaqLoading(true);
      const res = await fetch(`${API_URL}/api/faq/categories/${categoryId}`, {
        method: 'DELETE',
        headers: { 'X-Admin-Key': ADMIN_KEY }
      });
      
      if (!res.ok) throw new Error('Fehler beim Löschen');
      
      toast.success('Kategorie gelöscht');
      fetchFaqCategories();
    } catch (error) {
      toast.error('Fehler beim Löschen der Kategorie');
    } finally {
      setFaqLoading(false);
    }
  };

  const addQuestionToCategory = (category) => {
    const newQuestion = { id: Date.now().toString(), question: '', answer: '' };
    const updatedQuestions = [...(category.questions || []), newQuestion];
    if (editingCategory?.id === category.id) {
      setEditingCategory({ ...editingCategory, questions: updatedQuestions });
    }
  };

  const removeQuestionFromCategory = (category, questionId) => {
    const updatedQuestions = (category.questions || []).filter(q => q.id !== questionId);
    if (editingCategory?.id === category.id) {
      setEditingCategory({ ...editingCategory, questions: updatedQuestions });
    }
  };

  const updateQuestionInCategory = (questionId, field, value) => {
    if (!editingCategory) return;
    const updatedQuestions = editingCategory.questions.map(q => 
      q.id === questionId ? { ...q, [field]: value } : q
    );
    setEditingCategory({ ...editingCategory, questions: updatedQuestions });
  };

  const toggleCategoryExpand = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  return (
    <div className="min-h-screen bg-gray-100 overflow-x-hidden">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">Admin Dashboard</h1>
              <p className="text-xs sm:text-sm text-gray-500">Verwaltung</p>
            </div>
            <Button onClick={() => {
              if (activeTab === 'orders') fetchData();
              else if (activeTab === 'financing') fetchFinancingRequests();
              else fetchInventory();
            }} variant="outline" size="sm" className="shrink-0">
              <RefreshCw className={`sm:mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Aktualisieren</span>
            </Button>
          </div>
          
          {/* Tabs - fixed 6-column grid on mobile (no horizontal scroll), inline on desktop */}
          <div className="grid grid-cols-6 sm:flex sm:gap-2 mt-3 sm:mt-4 border-b">
            <button
              onClick={() => setActiveTab('orders')}
              className={`flex flex-col sm:flex-row items-center justify-center sm:justify-start gap-0.5 sm:gap-2 px-1 sm:px-4 py-2 font-medium text-[10px] sm:text-sm transition-colors border-b-2 -mb-px ${
                activeTab === 'orders' 
                  ? 'text-red-600 border-red-600' 
                  : 'text-gray-500 border-transparent hover:text-gray-700'
              }`}
              data-testid="tab-orders"
            >
              <Package className="h-4 w-4 shrink-0" />
              <span className="leading-tight text-center sm:text-left">Bestellungen</span>
            </button>
            <button
              onClick={() => setActiveTab('financing')}
              className={`relative flex flex-col sm:flex-row items-center justify-center sm:justify-start gap-0.5 sm:gap-2 px-1 sm:px-4 py-2 font-medium text-[10px] sm:text-sm transition-colors border-b-2 -mb-px ${
                activeTab === 'financing' 
                  ? 'text-red-600 border-red-600' 
                  : 'text-gray-500 border-transparent hover:text-gray-700'
              }`}
              data-testid="tab-financing"
            >
              <Calculator className="h-4 w-4 shrink-0" />
              <span className="leading-tight text-center sm:text-left">
                <span className="sm:hidden">Finanz.</span>
                <span className="hidden sm:inline">Finanzierungsanfragen</span>
              </span>
              {financingStats?.neu > 0 && (
                <span className="absolute top-0.5 right-1 sm:static sm:ml-2 bg-red-500 text-white text-[9px] sm:text-xs px-1 sm:px-2 py-0 sm:py-0.5 rounded-full leading-tight">
                  {financingStats.neu}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('inventory')}
              className={`relative flex flex-col sm:flex-row items-center justify-center sm:justify-start gap-0.5 sm:gap-2 px-1 sm:px-4 py-2 font-medium text-[10px] sm:text-sm transition-colors border-b-2 -mb-px ${
                activeTab === 'inventory' 
                  ? 'text-red-600 border-red-600' 
                  : 'text-gray-500 border-transparent hover:text-gray-700'
              }`}
              data-testid="tab-inventory"
            >
              <Boxes className="h-4 w-4 shrink-0" />
              <span className="leading-tight text-center sm:text-left">
                <span className="sm:hidden">Produkte</span>
                <span className="hidden sm:inline">Produkte & Preise</span>
              </span>
              {inventoryStats?.out_of_stock > 0 && (
                <span className="absolute top-0.5 right-1 sm:static sm:ml-2 bg-red-500 text-white text-[9px] sm:text-xs px-1 sm:px-2 py-0 sm:py-0.5 rounded-full leading-tight">
                  {inventoryStats.out_of_stock}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('faq')}
              className={`flex flex-col sm:flex-row items-center justify-center sm:justify-start gap-0.5 sm:gap-2 px-1 sm:px-4 py-2 font-medium text-[10px] sm:text-sm transition-colors border-b-2 -mb-px ${
                activeTab === 'faq' 
                  ? 'text-red-600 border-red-600' 
                  : 'text-gray-500 border-transparent hover:text-gray-700'
              }`}
              data-testid="tab-faq"
            >
              <HelpCircle className="h-4 w-4 shrink-0" />
              <span className="leading-tight text-center sm:text-left">
                <span className="sm:hidden">FAQ</span>
                <span className="hidden sm:inline">FAQ Verwaltung</span>
              </span>
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              className={`flex flex-col sm:flex-row items-center justify-center sm:justify-start gap-0.5 sm:gap-2 px-1 sm:px-4 py-2 font-medium text-[10px] sm:text-sm transition-colors border-b-2 -mb-px ${
                activeTab === 'gallery' 
                  ? 'text-red-600 border-red-600' 
                  : 'text-gray-500 border-transparent hover:text-gray-700'
              }`}
              data-testid="tab-gallery"
            >
              <Images className="h-4 w-4 shrink-0" />
              <span className="leading-tight text-center sm:text-left">
                <span className="sm:hidden">Bilder</span>
                <span className="hidden sm:inline">Bildverwaltung</span>
              </span>
            </button>
            <button
              onClick={() => setActiveTab('invoice')}
              className={`flex flex-col sm:flex-row items-center justify-center sm:justify-start gap-0.5 sm:gap-2 px-1 sm:px-4 py-2 font-medium text-[10px] sm:text-sm transition-colors border-b-2 -mb-px ${
                activeTab === 'invoice' 
                  ? 'text-red-600 border-red-600' 
                  : 'text-gray-500 border-transparent hover:text-gray-700'
              }`}
              data-testid="tab-invoice"
            >
              <FileText className="h-4 w-4 shrink-0" />
              <span className="leading-tight text-center sm:text-left">
                <span className="sm:hidden">Rechnung</span>
                <span className="hidden sm:inline">Manuelle Rechnung</span>
              </span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <>
            {/* Stats Cards */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Gesamt</p>
                        <p className="text-2xl font-bold">{stats.orders?.total || 0}</p>
                      </div>
                      <Package className="h-8 w-8 text-gray-400" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-orange-200 bg-orange-50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-orange-600">Zahlung offen</p>
                        <p className="text-2xl font-bold text-orange-700">{stats.payments?.awaiting || 0}</p>
                      </div>
                      <AlertCircle className="h-8 w-8 text-orange-400" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-green-200 bg-green-50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-green-600">Bezahlt</p>
                        <p className="text-2xl font-bold text-green-700">{stats.payments?.paid || 0}</p>
                      </div>
                      <CheckCircle className="h-8 w-8 text-green-400" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-blue-200 bg-blue-50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-blue-600">Versendet</p>
                        <p className="text-2xl font-bold text-blue-700">{stats.orders?.shipped || 0}</p>
                      </div>
                      <Truck className="h-8 w-8 text-blue-400" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-purple-200 bg-purple-50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-purple-600">Zugestellt</p>
                        <p className="text-2xl font-bold text-purple-700">{stats.orders?.delivered || 0}</p>
                      </div>
                      <CheckCircle className="h-8 w-8 text-purple-400" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-red-200 bg-red-50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-red-600">Umsatz</p>
                        <p className="text-xl font-bold text-red-700">{formatPrice(stats.revenue?.total)}</p>
                      </div>
                      <CreditCard className="h-8 w-8 text-red-400" />
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Filters */}
            <Card className="mb-6">
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-4 items-center">
                  <div className="flex-1 min-w-[200px]">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Suche nach Bestellnr., Name, E-Mail..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  
                  <select
                    value={filter.status}
                    onChange={(e) => setFilter(f => ({ ...f, status: e.target.value }))}
                    className="border rounded-md px-3 py-2 text-sm"
                  >
                    <option value="">Alle Status</option>
                    <option value="pending">Ausstehend</option>
                    <option value="confirmed">Bestätigt</option>
                    <option value="shipped">Versendet</option>
                    <option value="delivered">Zugestellt</option>
                  </select>
                  
                  <select
                    value={filter.payment}
                    onChange={(e) => setFilter(f => ({ ...f, payment: e.target.value }))}
                    className="border rounded-md px-3 py-2 text-sm"
                  >
                    <option value="">Alle Zahlungen</option>
                    <option value="awaiting_payment">Zahlung offen</option>
                    <option value="paid">Bezahlt</option>
                  </select>
                  
                  <select
                    value={filter.method}
                    onChange={(e) => setFilter(f => ({ ...f, method: e.target.value }))}
                    className="border rounded-md px-3 py-2 text-sm"
                  >
                    <option value="">Alle Methoden</option>
                    <option value="sepa">SEPA</option>
                    <option value="paypal">PayPal</option>
                  </select>
                  
                  <select
                    value={filter.preorder}
                    onChange={(e) => setFilter(f => ({ ...f, preorder: e.target.value }))}
                    className="border rounded-md px-3 py-2 text-sm bg-amber-50 border-amber-300"
                  >
                    <option value="">Alle Bestellungen</option>
                    <option value="preorder">🏷️ Nur Vorbestellungen</option>
                    <option value="regular">Nur reguläre Bestellungen</option>
                  </select>
                </div>
              </CardContent>
            </Card>

            {/* Orders Table */}
            <Card>
              <CardHeader>
                <CardTitle>Bestellungen ({filteredOrders.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8">
                    <RefreshCw className="h-8 w-8 animate-spin mx-auto text-gray-400" />
                    <p className="mt-2 text-gray-500">Lade Bestellungen...</p>
                  </div>
                ) : filteredOrders.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    Keine Bestellungen gefunden
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="text-left p-3 text-sm font-medium text-gray-600">Bestellung</th>
                          <th className="text-left p-3 text-sm font-medium text-gray-600">Kunde</th>
                          <th className="text-left p-3 text-sm font-medium text-gray-600">Datum</th>
                          <th className="text-left p-3 text-sm font-medium text-gray-600">Betrag</th>
                          <th className="text-left p-3 text-sm font-medium text-gray-600">Zahlung</th>
                          <th className="text-left p-3 text-sm font-medium text-gray-600">Status</th>
                          <th className="text-left p-3 text-sm font-medium text-gray-600">Aktionen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredOrders.map((order) => (
                          <tr key={order.id} className={`border-b hover:bg-gray-50 ${order.is_preorder ? 'bg-amber-50/50' : ''}`}>
                            <td className="p-3">
                              <div className="font-medium text-gray-900">
                                {order.order_number}
                                {order.is_preorder && (
                                  <span className="ml-2 px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-500 text-white">
                                    Vorbestellung
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-gray-500">
                                {order.payment_method === 'paypal' ? 'PayPal' : 'SEPA'}
                              </div>
                            </td>
                            <td className="p-3">
                              <div className="font-medium">{order.customer_name}</div>
                              <div className="text-xs text-gray-500">{order.customer_email}</div>
                            </td>
                            <td className="p-3 text-sm text-gray-600">
                              {formatDate(order.created_at)}
                            </td>
                            <td className="p-3">
                              {order.is_preorder ? (
                                <div>
                                  <div className="text-xs text-gray-500">Gesamtpreis (brutto):</div>
                                  <div className="font-medium text-gray-700">
                                    {formatPrice((order.discounted_total || 0) * 1.19)}
                                  </div>
                                  <div className="text-xs text-green-600 mt-1">
                                    ✓ Anzahlung (brutto): {formatPrice(order.total)}
                                  </div>
                                  <div className="text-xs text-orange-600">
                                    ○ Restzahlung (brutto): {formatPrice((order.remaining_amount || 0) * 1.19)}
                                  </div>
                                </div>
                              ) : (
                                <div className="font-medium">{formatPrice(order.total)}</div>
                              )}
                            </td>
                            <td className="p-3">
                              {getPaymentBadge(order.payment_status)}
                            </td>
                            <td className="p-3">
                              {order.is_preorder ? getPreorderStatusBadge(order.order_status) : getStatusBadge(order.order_status)}
                            </td>
                            <td className="p-3">
                              <div className="flex gap-2">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => setSelectedOrder(order)}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                {order.payment_status === 'awaiting_payment' && (
                                  <Button 
                                    size="sm" 
                                    className="bg-green-600 hover:bg-green-700"
                                    onClick={() => confirmPayment(order.id)}
                                  >
                                    <CreditCard className="h-4 w-4" />
                                  </Button>
                                )}
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="text-red-600 border-red-200 hover:bg-red-50"
                                  onClick={() => deleteOrderPermanently(order.id)}
                                  title="Dauerhaft löschen"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                
                {/* Delete All Orders Button */}
                <div className="mt-4 flex justify-end">
                  <Button 
                    variant="outline"
                    className="text-red-600 border-red-300 hover:bg-red-50"
                    onClick={deleteAllOrders}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Alle Bestellungen löschen
                  </Button>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Financing Tab */}
        {activeTab === 'financing' && (
          <>
            {/* Financing Stats */}
            {financingStats && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Gesamt</p>
                        <p className="text-2xl font-bold">{financingStats.total || 0}</p>
                      </div>
                      <Calculator className="h-8 w-8 text-gray-400" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-blue-200 bg-blue-50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-blue-600">Neu</p>
                        <p className="text-2xl font-bold text-blue-700">{financingStats.neu || 0}</p>
                      </div>
                      <AlertCircle className="h-8 w-8 text-blue-400" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-yellow-200 bg-yellow-50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-yellow-600">In Bearbeitung</p>
                        <p className="text-2xl font-bold text-yellow-700">{financingStats.in_bearbeitung || 0}</p>
                      </div>
                      <Clock className="h-8 w-8 text-yellow-400" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-purple-200 bg-purple-50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-purple-600">Angebot erstellt</p>
                        <p className="text-2xl font-bold text-purple-700">{financingStats.angebot_erstellt || 0}</p>
                      </div>
                      <FileText className="h-8 w-8 text-purple-400" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-green-200 bg-green-50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-green-600">Abgeschlossen</p>
                        <p className="text-2xl font-bold text-green-700">{financingStats.abgeschlossen || 0}</p>
                      </div>
                      <CheckCircle className="h-8 w-8 text-green-400" />
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Search */}
            <Card className="mb-6">
              <CardContent className="p-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Suche nach Name, E-Mail, Firma, Produkt..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Financing Requests Table */}
            <Card>
              <CardHeader>
                <CardTitle>Finanzierungsanfragen ({filteredFinancing.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8">
                    <RefreshCw className="h-8 w-8 animate-spin mx-auto text-gray-400" />
                    <p className="mt-2 text-gray-500">Lade Anfragen...</p>
                  </div>
                ) : filteredFinancing.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    Keine Finanzierungsanfragen gefunden
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="text-left p-3 text-sm font-medium text-gray-600">Kunde</th>
                          <th className="text-left p-3 text-sm font-medium text-gray-600">Maschine</th>
                          <th className="text-left p-3 text-sm font-medium text-gray-600">Preis</th>
                          <th className="text-left p-3 text-sm font-medium text-gray-600">Laufzeit</th>
                          <th className="text-left p-3 text-sm font-medium text-gray-600">Datum</th>
                          <th className="text-left p-3 text-sm font-medium text-gray-600">Status</th>
                          <th className="text-left p-3 text-sm font-medium text-gray-600">Aktionen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredFinancing.map((req) => (
                          <tr key={req.id} className="border-b hover:bg-gray-50">
                            <td className="p-3">
                              <div className="font-medium text-gray-900">{req.first_name} {req.last_name}</div>
                              <div className="text-xs text-gray-500">{req.company || req.email}</div>
                            </td>
                            <td className="p-3">
                              <div className="font-medium">{req.product_name}</div>
                            </td>
                            <td className="p-3 font-medium text-red-600">
                              {formatPrice(req.purchase_price)}
                            </td>
                            <td className="p-3 text-sm">
                              {req.term_months} Monate
                            </td>
                            <td className="p-3 text-sm text-gray-600">
                              {formatDate(req.created_at)}
                            </td>
                            <td className="p-3">
                              {getFinancingStatusBadge(req.status)}
                            </td>
                            <td className="p-3">
                              <div className="flex gap-2">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => setSelectedFinancing(req)}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="text-red-600 border-red-200 hover:bg-red-50"
                                  onClick={() => deleteFinancingRequest(req.id)}
                                  title="Dauerhaft löschen"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                
                {/* Delete All Financing Requests Button */}
                <div className="mt-4 flex justify-end">
                  <Button 
                    variant="outline"
                    className="text-red-600 border-red-300 hover:bg-red-50"
                    onClick={deleteAllFinancingRequests}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Alle Finanzierungsanfragen löschen
                  </Button>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Inventory Tab */}
        {activeTab === 'inventory' && (
          <>
            {/* Inventory Stats */}
            {inventoryStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Produkte</p>
                        <p className="text-2xl font-bold">{inventoryStats.total_products || 0}</p>
                      </div>
                      <Package className="h-8 w-8 text-gray-400" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Zubehör</p>
                        <p className="text-2xl font-bold">{inventoryStats.total_accessories || 0}</p>
                      </div>
                      <Boxes className="h-8 w-8 text-gray-400" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-red-200 bg-red-50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-red-600">Nicht verfügbar</p>
                        <p className="text-2xl font-bold text-red-700">{inventoryStats.out_of_stock || 0}</p>
                      </div>
                      <AlertCircle className="h-8 w-8 text-red-400" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-orange-200 bg-orange-50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-orange-600">Niedriger Bestand</p>
                        <p className="text-2xl font-bold text-orange-700">{inventoryStats.low_stock || 0}</p>
                      </div>
                      <AlertTriangle className="h-8 w-8 text-orange-400" />
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Products Inventory */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Maschinen ({inventory.products?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8">
                    <RefreshCw className="h-8 w-8 animate-spin mx-auto text-gray-400" />
                    <p className="mt-2 text-gray-500">Lade Inventar...</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="text-center p-3 text-sm font-medium text-gray-600 w-20">Bild</th>
                          <th className="text-left p-3 text-sm font-medium text-gray-600">Modell</th>
                          <th className="text-left p-3 text-sm font-medium text-gray-600">Produktname</th>
                          <th className="text-left p-3 text-sm font-medium text-gray-600">Lieferzeit</th>
                          <th className="text-left p-3 text-sm font-medium text-gray-600">Verfügbarkeit</th>
                          <th className="text-right p-3 text-sm font-medium text-gray-600">Preis (netto)</th>
                          <th className="text-center p-3 text-sm font-medium text-gray-600">Bestand</th>
                          <th className="text-center p-3 text-sm font-medium text-gray-600">Aktionen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inventory.products?.map((product) => {
                          const netPrice = product.pricing?.numericPrice || 0;
                          const isEditing = editingProduct === `product-${product.slug}`;
                          const isUploadingThis = uploadingImage === `product-${product.slug}`;
                          const isEditingUrl = editingImageUrl?.slug === product.slug && editingImageUrl?.type === 'product';
                          return (
                          <tr key={product.slug} className="border-b hover:bg-gray-50">
                            {/* Image Column */}
                            <td className="p-2 text-center">
                              <div className="relative group">
                                {product.image ? (
                                  <img 
                                    src={product.image} 
                                    alt={product.name}
                                    className="w-16 h-16 object-contain mx-auto rounded border border-gray-200"
                                  />
                                ) : (
                                  <div className="w-16 h-16 bg-gray-100 rounded border border-gray-200 flex items-center justify-center mx-auto">
                                    <ImageIcon className="w-6 h-6 text-gray-400" />
                                  </div>
                                )}
                                {/* Upload/Edit buttons overlay */}
                                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1 rounded">
                                  <label className="cursor-pointer p-1 hover:bg-white/20 rounded" title="Bild hochladen">
                                    <input
                                      type="file"
                                      accept="image/*"
                                      className="hidden"
                                      onChange={(e) => handleFileSelect(e, product.slug, 'product')}
                                      disabled={isUploadingThis}
                                    />
                                    {isUploadingThis ? (
                                      <RefreshCw className="w-4 h-4 text-white animate-spin" />
                                    ) : (
                                      <Upload className="w-4 h-4 text-white" />
                                    )}
                                  </label>
                                  <button 
                                    onClick={() => setEditingImageUrl({ slug: product.slug, type: 'product', url: product.image || '' })}
                                    className="p-1 hover:bg-white/20 rounded"
                                    title="URL bearbeiten"
                                  >
                                    <Edit2 className="w-4 h-4 text-white" />
                                  </button>
                                  {product.image && (
                                    <button 
                                      onClick={() => handleImageDelete(product.slug, 'product')}
                                      className="p-1 hover:bg-red-500/50 rounded"
                                      title="Bild löschen"
                                    >
                                      <Trash2 className="w-4 h-4 text-white" />
                                    </button>
                                  )}
                                </div>
                              </div>
                              {/* URL Edit Modal */}
                              {isEditingUrl && (
                                <div className="absolute z-50 bg-white border rounded-lg shadow-lg p-3 mt-1 w-64 left-0">
                                  <p className="text-xs font-medium mb-2">Bild-URL eingeben:</p>
                                  <Input
                                    value={editingImageUrl.url}
                                    onChange={(e) => setEditingImageUrl(prev => ({ ...prev, url: e.target.value }))}
                                    placeholder="https://..."
                                    className="text-xs mb-2"
                                  />
                                  <div className="flex gap-2">
                                    <Button 
                                      size="sm" 
                                      onClick={() => {
                                        handleImageUrlUpdate(product.slug, 'product', editingImageUrl.url);
                                        setEditingImageUrl(null);
                                      }}
                                    >
                                      Speichern
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => setEditingImageUrl(null)}>
                                      Abbrechen
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </td>
                            <td className="p-3">
                              {isEditing ? (
                                <Input
                                  value={productEditForm.model}
                                  onChange={(e) => setProductEditForm(f => ({...f, model: e.target.value}))}
                                  className="w-24"
                                  placeholder="Modell"
                                />
                              ) : (
                                <span className="font-bold text-red-600">{product.model || '-'}</span>
                              )}
                            </td>
                            <td className="p-3">
                              {isEditing ? (
                                <Input
                                  value={productEditForm.name}
                                  onChange={(e) => setProductEditForm(f => ({...f, name: e.target.value}))}
                                  className="w-48"
                                  placeholder="Produktname"
                                />
                              ) : (
                                <div>
                                  <div className="font-medium text-gray-900">{product.name}</div>
                                  <div className="text-xs text-gray-500">{product.category}</div>
                                </div>
                              )}
                            </td>
                            <td className="p-3">
                              {isEditing ? (
                                <Input
                                  value={productEditForm.deliveryTime}
                                  onChange={(e) => setProductEditForm(f => ({...f, deliveryTime: e.target.value}))}
                                  className="w-40"
                                  placeholder="z.B. 5-7 Werktage"
                                />
                              ) : (
                                <span className="text-sm text-gray-600">{product.deliveryTime || '-'}</span>
                              )}
                            </td>
                            <td className="p-3">
                              {isEditing ? (
                                <select
                                  value={productEditForm.availabilityStatus || 'sofort_lieferbar'}
                                  onChange={(e) => setProductEditForm(f => ({...f, availabilityStatus: e.target.value}))}
                                  className="border rounded-md px-2 py-1 text-sm"
                                >
                                  <option value="sofort_lieferbar">Sofort lieferbar</option>
                                  <option value="vorbestellen">Vorbestellen</option>
                                </select>
                              ) : (
                                product.availabilityStatus === 'vorbestellen' ? (
                                  <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                                    Vorbestellen
                                  </span>
                                ) : (
                                  <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                                    Sofort lieferbar
                                  </span>
                                )
                              )}
                            </td>
                            <td className="p-3 text-right">
                              {editingPrice === `product-${product.slug}` ? (
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={newPriceValue}
                                  onChange={(e) => setNewPriceValue(e.target.value)}
                                  className="w-28 text-right ml-auto"
                                  autoFocus
                                  placeholder="Netto €"
                                />
                              ) : (
                                <span className="font-bold text-gray-900">
                                  {formatPrice(netPrice)}
                                </span>
                              )}
                            </td>
                            <td className="p-3 text-center">
                              {editingStock === `product-${product.slug}` ? (
                                <div className="flex items-center justify-center gap-1">
                                  <Input
                                    type="number"
                                    min="0"
                                    value={newStockValue}
                                    onChange={(e) => setNewStockValue(e.target.value)}
                                    className="w-16 text-center"
                                    autoFocus
                                  />
                                  <Button
                                    size="sm"
                                    onClick={() => updateStock(product.slug, 'product', parseInt(newStockValue) || 0)}
                                    className="bg-green-600 hover:bg-green-700 h-8 w-8 p-0"
                                    title="Speichern"
                                  >
                                    <Save className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => { setEditingStock(null); setNewStockValue(''); }}
                                    className="h-8 w-8 p-0"
                                  >
                                    <X className="h-3 w-3" />
                                  </Button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => {
                                    setEditingStock(`product-${product.slug}`);
                                    setNewStockValue(String(product.stockQuantity ?? 10));
                                  }}
                                  className={`font-bold px-3 py-1 rounded cursor-pointer hover:bg-gray-100 ${
                                    (product.stockQuantity ?? 10) === 0 ? 'text-red-600 bg-red-50' :
                                    (product.stockQuantity ?? 10) <= 2 ? 'text-orange-600 bg-orange-50' : 'text-green-600 bg-green-50'
                                  }`}
                                  title="Klicken zum Bearbeiten"
                                >
                                  {product.stockQuantity ?? 10} Stk.
                                </button>
                              )}
                            </td>
                            <td className="p-3 text-center">
                              <div className="flex gap-1 justify-center flex-wrap">
                                {/* Product details editing */}
                                {isEditing ? (
                                  <>
                                    <Button
                                      size="sm"
                                      onClick={() => updateProductDetails(product.slug, 'product', productEditForm)}
                                      className="bg-green-600 hover:bg-green-700"
                                      title="Speichern"
                                    >
                                      <Save className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => { setEditingProduct(null); setProductEditForm({ name: '', model: '', deliveryTime: '', availabilityStatus: '' }); }}
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </>
                                ) : editingPrice === `product-${product.slug}` ? (
                                  <>
                                    <Button
                                      size="sm"
                                      onClick={() => updatePrice(product.slug, 'product', parseFloat(newPriceValue) || 0)}
                                      className="bg-green-600 hover:bg-green-700"
                                      title="Preis speichern"
                                    >
                                      <Save className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => { setEditingPrice(null); setNewPriceValue(''); }}
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        setEditingProduct(`product-${product.slug}`);
                                        setProductEditForm({
                                          name: product.name || '',
                                          model: product.model || '',
                                          deliveryTime: product.deliveryTime || '',
                                          availabilityStatus: product.availabilityStatus || 'sofort_lieferbar'
                                        });
                                      }}
                                      title="Produkt bearbeiten"
                                      className="text-purple-600 border-purple-200 hover:bg-purple-50"
                                    >
                                      <Edit2 className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        setEditingPrice(`product-${product.slug}`);
                                        setNewPriceValue(String(netPrice));
                                      }}
                                      title="Preis bearbeiten"
                                      className="text-blue-600 border-blue-200 hover:bg-blue-50"
                                    >
                                      €
                                    </Button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        )})}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Accessories Inventory */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Boxes className="h-5 w-5" />
                  Zubehör ({inventory.accessories?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-center p-3 text-sm font-medium text-gray-600 w-20">Bild</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-600">Produktname</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-600">Lieferzeit</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-600">Verfügbarkeit</th>
                        <th className="text-right p-3 text-sm font-medium text-gray-600">Preis (netto)</th>
                        <th className="text-center p-3 text-sm font-medium text-gray-600">Bestand</th>
                        <th className="text-center p-3 text-sm font-medium text-gray-600">Aktionen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inventory.accessories?.map((accessory) => {
                        const netPrice = accessory.price || 0;
                        const isEditing = editingProduct === `accessory-${accessory.slug}`;
                        const isUploadingThis = uploadingImage === `accessory-${accessory.slug}`;
                        return (
                        <tr key={accessory.slug} className="border-b hover:bg-gray-50">
                          {/* Image Column */}
                          <td className="p-2 text-center">
                            <div className="relative group">
                              {accessory.image ? (
                                <img 
                                  src={accessory.image} 
                                  alt={accessory.name}
                                  className="w-16 h-16 object-contain mx-auto rounded border border-gray-200"
                                />
                              ) : (
                                <div className="w-16 h-16 bg-gray-100 rounded border border-gray-200 flex items-center justify-center mx-auto">
                                  <ImageIcon className="w-6 h-6 text-gray-400" />
                                </div>
                              )}
                              {/* Upload button overlay */}
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded">
                                <label className="cursor-pointer">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => handleFileSelect(e, accessory.slug, 'accessory')}
                                    disabled={isUploadingThis}
                                  />
                                  {isUploadingThis ? (
                                    <RefreshCw className="w-5 h-5 text-white animate-spin" />
                                  ) : (
                                    <Upload className="w-5 h-5 text-white" />
                                  )}
                                </label>
                              </div>
                            </div>
                          </td>
                          <td className="p-3">
                            {isEditing ? (
                              <Input
                                value={productEditForm.name}
                                onChange={(e) => setProductEditForm(f => ({...f, name: e.target.value}))}
                                className="w-40"
                                placeholder="Produktname"
                              />
                            ) : (
                              <div className="font-medium text-gray-900">{accessory.name}</div>
                            )}
                          </td>
                          <td className="p-3">
                            {isEditing ? (
                              <Input
                                value={productEditForm.deliveryTime}
                                onChange={(e) => setProductEditForm(f => ({...f, deliveryTime: e.target.value}))}
                                className="w-36"
                                placeholder="z.B. 5-7 Werktage"
                              />
                            ) : (
                              <span className="text-sm text-gray-600">{accessory.deliveryTime || '-'}</span>
                            )}
                          </td>
                          <td className="p-3">
                            {isEditing ? (
                              <select
                                value={productEditForm.availabilityStatus || 'sofort_lieferbar'}
                                onChange={(e) => setProductEditForm(f => ({...f, availabilityStatus: e.target.value}))}
                                className="border rounded-md px-2 py-1 text-sm"
                              >
                                <option value="sofort_lieferbar">Sofort lieferbar</option>
                                <option value="vorbestellen">Vorbestellen</option>
                              </select>
                            ) : (
                              accessory.availabilityStatus === 'vorbestellen' ? (
                                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                                  Vorbestellen
                                </span>
                              ) : (
                                <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                                  Sofort lieferbar
                                </span>
                              )
                            )}
                          </td>
                          <td className="p-3 text-right">
                            {editingPrice === `accessory-${accessory.slug}` ? (
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={newPriceValue}
                                onChange={(e) => setNewPriceValue(e.target.value)}
                                className="w-28 text-right ml-auto"
                                autoFocus
                                placeholder="Netto €"
                              />
                            ) : (
                              <span className="font-bold text-gray-900">
                                {formatPrice(netPrice)}
                              </span>
                            )}
                          </td>
                          <td className="p-3 text-center">
                            {editingStock === `accessory-${accessory.slug}` ? (
                              <div className="flex items-center justify-center gap-1">
                                <Input
                                  type="number"
                                  min="0"
                                  value={newStockValue}
                                  onChange={(e) => setNewStockValue(e.target.value)}
                                  className="w-16 text-center"
                                  autoFocus
                                />
                                <Button
                                  size="sm"
                                  onClick={() => updateStock(accessory.slug, 'accessory', parseInt(newStockValue) || 0)}
                                  className="bg-green-600 hover:bg-green-700 h-8 w-8 p-0"
                                  title="Speichern"
                                >
                                  <Save className="h-3 w-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => { setEditingStock(null); setNewStockValue(''); }}
                                  className="h-8 w-8 p-0"
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              </div>
                            ) : (
                              <button
                                onClick={() => {
                                  setEditingStock(`accessory-${accessory.slug}`);
                                  setNewStockValue(String(accessory.stockQuantity ?? 10));
                                }}
                                className={`font-bold px-3 py-1 rounded cursor-pointer hover:bg-gray-100 ${
                                  (accessory.stockQuantity ?? 10) === 0 ? 'text-red-600 bg-red-50' :
                                  (accessory.stockQuantity ?? 10) <= 2 ? 'text-orange-600 bg-orange-50' : 'text-green-600 bg-green-50'
                                }`}
                                title="Klicken zum Bearbeiten"
                              >
                                {accessory.stockQuantity ?? 10} Stk.
                              </button>
                            )}
                          </td>
                          <td className="p-3 text-center">
                            <div className="flex gap-1 justify-center flex-wrap">
                              {/* Accessory details editing */}
                              {isEditing ? (
                                <>
                                  <Button
                                    size="sm"
                                    onClick={() => updateProductDetails(accessory.slug, 'accessory', productEditForm)}
                                    className="bg-green-600 hover:bg-green-700"
                                    title="Speichern"
                                  >
                                    <Save className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => { setEditingProduct(null); setProductEditForm({ name: '', model: '', deliveryTime: '', availabilityStatus: '' }); }}
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </>
                              ) : editingPrice === `accessory-${accessory.slug}` ? (
                                <>
                                  <Button
                                    size="sm"
                                    onClick={() => updatePrice(accessory.slug, 'accessory', parseFloat(newPriceValue) || 0)}
                                    className="bg-green-600 hover:bg-green-700"
                                    title="Preis speichern"
                                  >
                                    <Save className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => { setEditingPrice(null); setNewPriceValue(''); }}
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setEditingProduct(`accessory-${accessory.slug}`);
                                      setProductEditForm({
                                        name: accessory.name || '',
                                        model: '',
                                        deliveryTime: accessory.deliveryTime || '',
                                        availabilityStatus: accessory.availabilityStatus || 'sofort_lieferbar'
                                      });
                                    }}
                                    title="Zubehör bearbeiten"
                                    className="text-purple-600 border-purple-200 hover:bg-purple-50"
                                  >
                                    <Edit2 className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setEditingPrice(`accessory-${accessory.slug}`);
                                      setNewPriceValue(String(netPrice));
                                    }}
                                    title="Preis bearbeiten"
                                    className="text-blue-600 border-blue-200 hover:bg-blue-50"
                                  >
                                    €
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      )})}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* FAQ Tab */}
        {activeTab === 'faq' && (
          <div className="space-y-6">
            {/* FAQ Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <HelpCircle className="h-6 w-6 text-red-600" />
                  FAQ Verwaltung
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Verwalten Sie die häufig gestellten Fragen auf Ihrer Website
                </p>
              </div>
              <div className="flex gap-2">
                {faqCategories.length === 0 && (
                  <Button
                    onClick={seedFaqData}
                    disabled={faqLoading}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {faqLoading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                    Standard-FAQs anlegen
                  </Button>
                )}
                <Button
                  onClick={() => {
                    setShowNewCategoryForm(true);
                    setNewCategory({ title: '', icon: '❓', order: faqCategories.length + 1, questions: [] });
                  }}
                  className="bg-red-600 hover:bg-red-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Neue Kategorie
                </Button>
                <Button
                  onClick={fetchFaqCategories}
                  variant="outline"
                  disabled={faqLoading}
                >
                  <RefreshCw className={`h-4 w-4 ${faqLoading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>

            {/* New Category Form */}
            {showNewCategoryForm && (
              <Card className="border-green-200 bg-green-50">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Plus className="h-5 w-5 text-green-600" />
                    Neue Kategorie erstellen
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Icon (Emoji)</label>
                      <Input
                        value={newCategory.icon}
                        onChange={(e) => setNewCategory({ ...newCategory, icon: e.target.value })}
                        placeholder="z.B. 🚚"
                        className="text-2xl text-center"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Kategorie-Titel</label>
                      <Input
                        value={newCategory.title}
                        onChange={(e) => setNewCategory({ ...newCategory, title: e.target.value })}
                        placeholder="z.B. Lieferung & Spedition"
                      />
                    </div>
                  </div>
                  
                  {/* Questions for new category */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium text-gray-700">Fragen</label>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setNewCategory({
                          ...newCategory,
                          questions: [...newCategory.questions, { id: Date.now().toString(), question: '', answer: '' }]
                        })}
                      >
                        <Plus className="h-4 w-4 mr-1" /> Frage hinzufügen
                      </Button>
                    </div>
                    {newCategory.questions.map((q, idx) => (
                      <div key={q.id} className="bg-white p-4 rounded-lg border mb-3">
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-sm font-medium text-gray-500">Frage {idx + 1}</span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setNewCategory({
                              ...newCategory,
                              questions: newCategory.questions.filter(item => item.id !== q.id)
                            })}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        <Input
                          value={q.question}
                          onChange={(e) => setNewCategory({
                            ...newCategory,
                            questions: newCategory.questions.map(item => 
                              item.id === q.id ? { ...item, question: e.target.value } : item
                            )
                          })}
                          placeholder="Frage eingeben..."
                          className="mb-2"
                        />
                        <textarea
                          value={q.answer}
                          onChange={(e) => setNewCategory({
                            ...newCategory,
                            questions: newCategory.questions.map(item => 
                              item.id === q.id ? { ...item, answer: e.target.value } : item
                            )
                          })}
                          placeholder="Antwort eingeben..."
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        />
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-end gap-2 pt-4 border-t">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowNewCategoryForm(false);
                        setNewCategory({ title: '', icon: '❓', questions: [] });
                      }}
                    >
                      Abbrechen
                    </Button>
                    <Button
                      onClick={() => saveFaqCategory(newCategory)}
                      disabled={!newCategory.title || faqLoading}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {faqLoading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                      Kategorie speichern
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Loading State */}
            {faqLoading && !showNewCategoryForm && (
              <div className="text-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin mx-auto text-gray-400" />
                <p className="mt-2 text-gray-500">Lade FAQ-Kategorien...</p>
              </div>
            )}

            {/* Empty State */}
            {!faqLoading && faqCategories.length === 0 && !showNewCategoryForm && (
              <Card className="border-dashed border-2 border-gray-300">
                <CardContent className="py-12 text-center">
                  <HelpCircle className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Keine FAQ-Kategorien vorhanden</h3>
                  <p className="text-gray-500 mb-4">
                    Klicken Sie auf "Standard-FAQs anlegen" um die Basis-FAQs zu erstellen, oder erstellen Sie eine neue Kategorie.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* FAQ Categories List */}
            {!faqLoading && faqCategories.length > 0 && (
              <div className="space-y-4">
                {faqCategories.map((category) => (
                  <Card key={category.id} className={editingCategory?.id === category.id ? 'border-red-300 ring-2 ring-red-100' : ''}>
                    <CardHeader className="py-3">
                      <div className="flex justify-between items-center">
                        <button
                          onClick={() => toggleCategoryExpand(category.id)}
                          className="flex items-center gap-3 text-left flex-1"
                        >
                          <span className="text-2xl">{category.icon}</span>
                          <div>
                            <CardTitle className="text-lg">{category.title}</CardTitle>
                            <p className="text-sm text-gray-500">{category.questions?.length || 0} Fragen</p>
                          </div>
                          {expandedCategories[category.id] ? (
                            <ChevronUp className="h-5 w-5 text-gray-400 ml-auto" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-gray-400 ml-auto" />
                          )}
                        </button>
                        <div className="flex gap-2 ml-4">
                          {editingCategory?.id === category.id ? (
                            <>
                              <Button
                                size="sm"
                                onClick={() => saveFaqCategory(editingCategory)}
                                disabled={faqLoading}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                <Save className="h-4 w-4 mr-1" /> Speichern
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setEditingCategory(null)}
                              >
                                Abbrechen
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setEditingCategory({ ...category });
                                  setExpandedCategories(prev => ({ ...prev, [category.id]: true }));
                                }}
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => deleteFaqCategory(category.id)}
                                className="text-red-600 border-red-200 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    
                    {expandedCategories[category.id] && (
                      <CardContent className="pt-0">
                        {/* Edit Category Header */}
                        {editingCategory?.id === category.id && (
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4 pb-4 border-b">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Icon</label>
                              <Input
                                value={editingCategory.icon}
                                onChange={(e) => setEditingCategory({ ...editingCategory, icon: e.target.value })}
                                className="text-2xl text-center"
                              />
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-sm font-medium text-gray-700 mb-1">Titel</label>
                              <Input
                                value={editingCategory.title}
                                onChange={(e) => setEditingCategory({ ...editingCategory, title: e.target.value })}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Reihenfolge</label>
                              <Input
                                type="number"
                                value={editingCategory.order || 0}
                                onChange={(e) => setEditingCategory({ ...editingCategory, order: parseInt(e.target.value) || 0 })}
                              />
                            </div>
                          </div>
                        )}

                        {/* Questions List */}
                        <div className="space-y-3">
                          {(editingCategory?.id === category.id ? editingCategory.questions : category.questions)?.map((q, idx) => (
                            <div key={q.id} className="bg-gray-50 p-4 rounded-lg border">
                              {editingCategory?.id === category.id ? (
                                <>
                                  <div className="flex justify-between items-start mb-2">
                                    <span className="text-sm font-medium text-gray-500 flex items-center gap-2">
                                      <GripVertical className="h-4 w-4 text-gray-400" />
                                      Frage {idx + 1}
                                    </span>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => removeQuestionFromCategory(category, q.id)}
                                      className="text-red-600 hover:bg-red-50"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                  <Input
                                    value={q.question}
                                    onChange={(e) => updateQuestionInCategory(q.id, 'question', e.target.value)}
                                    placeholder="Frage eingeben..."
                                    className="mb-2"
                                  />
                                  <textarea
                                    value={q.answer}
                                    onChange={(e) => updateQuestionInCategory(q.id, 'answer', e.target.value)}
                                    placeholder="Antwort eingeben..."
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                  />
                                </>
                              ) : (
                                <>
                                  <h4 className="font-medium text-gray-900 mb-2">{q.question}</h4>
                                  <p className="text-sm text-gray-600">{q.answer}</p>
                                </>
                              )}
                            </div>
                          ))}
                          
                          {editingCategory?.id === category.id && (
                            <Button
                              variant="outline"
                              onClick={() => addQuestionToCategory(editingCategory)}
                              className="w-full border-dashed"
                            >
                              <Plus className="h-4 w-4 mr-2" /> Neue Frage hinzufügen
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <OrderDetailModal 
          order={selectedOrder} 
          onClose={() => setSelectedOrder(null)}
          onUpdateStatus={updateOrderStatus}
          onConfirmPayment={confirmPayment}
          onAddTracking={addTracking}
          onDeleteOrder={deleteOrderPermanently}
          formatPrice={formatPrice}
          formatDate={formatDate}
          getStatusBadge={getStatusBadge}
          getPaymentBadge={getPaymentBadge}
          getPreorderStatusBadge={getPreorderStatusBadge}
        />
      )}

      {/* Financing Detail Modal */}
      {selectedFinancing && (
        <FinancingDetailModal
          request={selectedFinancing}
          onClose={() => setSelectedFinancing(null)}
          onUpdateStatus={updateFinancingStatus}
          onDeleteRequest={deleteFinancingRequest}
          formatPrice={formatPrice}
          formatDate={formatDate}
          getStatusBadge={getFinancingStatusBadge}
        />
      )}

      {/* Gallery Tab */}
      {activeTab === 'gallery' && (
        <ProductImageManager />
      )}

      {/* Manual Invoice Tab */}
      {activeTab === 'invoice' && (
        <div className="max-w-4xl mx-auto">
          <ManualInvoiceForm />
        </div>
      )}

      {/* Image Preview Modal */}
      {imagePreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setImagePreview(null)}>
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">Bild hochladen</h3>
              <button onClick={() => setImagePreview(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <img 
                src={imagePreview.url} 
                alt="Vorschau" 
                className="w-full h-48 object-contain border rounded-lg bg-gray-50"
              />
            </div>
            
            <p className="text-sm text-gray-500 mb-4">
              Möchten Sie dieses Bild für das Produkt verwenden?
            </p>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setImagePreview(null)}
                className="flex-1"
              >
                Abbrechen
              </Button>
              <Button
                onClick={() => {
                  handleImageUpload(imagePreview.file, imagePreview.slug, imagePreview.type);
                  setImagePreview(null);
                }}
                className="flex-1 bg-red-600 hover:bg-red-700"
                disabled={uploadingImage}
              >
                {uploadingImage ? (
                  <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                Hochladen
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Order Detail Modal Component
const OrderDetailModal = ({ 
  order, 
  onClose, 
  onUpdateStatus, 
  onConfirmPayment,
  onAddTracking,
  onDeleteOrder,
  formatPrice, 
  formatDate,
  getStatusBadge,
  getPaymentBadge,
  getPreorderStatusBadge
}) => {
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingCarrier, setTrackingCarrier] = useState('DHL');
  const [showTrackingForm, setShowTrackingForm] = useState(false);

  const handleAddTracking = () => {
    if (!trackingNumber.trim()) {
      toast.error('Bitte Tracking-Nummer eingeben');
      return;
    }
    onAddTracking(order.id, trackingNumber, trackingCarrier);
    setShowTrackingForm(false);
    setTrackingNumber('');
  };

  const handleDelete = () => {
    if (onDeleteOrder) {
      onDeleteOrder(order.id);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className={`sticky top-0 border-b px-6 py-4 flex items-center justify-between ${order.is_preorder ? 'bg-amber-50' : 'bg-white'}`}>
          <div>
            <h2 className="text-xl font-bold">
              {order.order_number}
              {order.is_preorder && (
                <span className="ml-2 px-3 py-1 text-sm font-semibold rounded-full bg-amber-500 text-white">
                  Vorbestellung
                </span>
              )}
            </h2>
            <p className="text-sm text-gray-500">{formatDate(order.created_at)}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Preorder Price Breakdown */}
          {order.is_preorder && (
            <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4">
              <h3 className="font-bold text-amber-800 mb-3 flex items-center">
                <Package className="mr-2 h-5 w-5" />
                Vorbestellungs-Preisübersicht
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Originalpreis (netto):</span>
                  <span className="float-right line-through text-gray-500">{formatPrice(order.original_total || 0)}</span>
                </div>
                <div>
                  <span className="text-gray-600">Rabatt ({order.preorder_discount_percent || 10}%):</span>
                  <span className="float-right text-green-600">-{formatPrice(order.discount_amount || 0)}</span>
                </div>
                <div className="col-span-2 border-t border-amber-200 pt-2">
                  <span className="text-gray-700 font-medium">Reduzierter Preis (netto):</span>
                  <span className="float-right font-medium">{formatPrice(order.discounted_total || 0)}</span>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-600">+ 19% MwSt:</span>
                  <span className="float-right">{formatPrice((order.discounted_total || 0) * 0.19)}</span>
                </div>
                <div className="col-span-2 border-t border-amber-200 pt-2 text-base">
                  <span className="font-bold text-gray-900">Gesamtpreis (brutto):</span>
                  <span className="float-right font-bold text-gray-900">{formatPrice((order.discounted_total || 0) * 1.19)}</span>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-amber-300 grid grid-cols-2 gap-4">
                <div className="bg-green-100 rounded-lg p-3">
                  <span className="text-green-800 text-sm">✓ Anzahlung (brutto) - bezahlt:</span>
                  <span className="block text-xl font-bold text-green-700">{formatPrice(order.total)}</span>
                </div>
                <div className="bg-orange-100 rounded-lg p-3">
                  <span className="text-orange-800 text-sm">○ Restzahlung (brutto) - offen:</span>
                  <span className="block text-xl font-bold text-orange-700">{formatPrice((order.remaining_amount || 0) * 1.19)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Status Row */}
          <div className="flex flex-wrap gap-4">
            <div>
              <span className="text-sm text-gray-500 block mb-1">Bestellstatus</span>
              {order.is_preorder ? getPreorderStatusBadge(order.order_status) : getStatusBadge(order.order_status)}
            </div>
            <div>
              <span className="text-sm text-gray-500 block mb-1">Zahlungsstatus</span>
              {getPaymentBadge(order.payment_status)}
            </div>
            <div>
              <span className="text-sm text-gray-500 block mb-1">Zahlungsart</span>
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100">
                {order.payment_method === 'paypal' ? 'PayPal' : 'SEPA-Überweisung'}
              </span>
            </div>
          </div>

          {/* Customer Info */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">Kunde</h3>
              <p className="font-medium">{order.customer_name}</p>
              <p className="text-sm text-gray-600">{order.customer_email}</p>
              <p className="text-sm text-gray-600">{order.customer_phone}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">Lieferadresse</h3>
              {order.shipping_address?.company && (
                <p className="font-medium">{order.shipping_address.company}</p>
              )}
              <p>{order.shipping_address?.name}</p>
              <p className="text-sm text-gray-600">{order.shipping_address?.street}</p>
              <p className="text-sm text-gray-600">
                {order.shipping_address?.postal_code} {order.shipping_address?.city}
              </p>
              {order.shipping_address?.vat_id && (
                <p className="text-sm text-gray-500 mt-1">USt-IdNr.: {order.shipping_address.vat_id}</p>
              )}
            </div>
          </div>

          {/* Order Items */}
          <div>
            <h3 className="font-semibold mb-3">Bestellte Artikel</h3>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-3 text-sm">Artikel</th>
                    <th className="text-center p-3 text-sm">Menge</th>
                    <th className="text-right p-3 text-sm">Preis</th>
                  </tr>
                </thead>
                <tbody>
                  {order.items?.map((item, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="p-3">{item.product_name}</td>
                      <td className="p-3 text-center">{item.quantity}</td>
                      <td className="p-3 text-right">{formatPrice(item.total_price)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50">
                  <tr className="border-t">
                    <td colSpan="2" className="p-3 text-right text-sm">Zwischensumme:</td>
                    <td className="p-3 text-right">{formatPrice(order.subtotal)}</td>
                  </tr>
                  <tr>
                    <td colSpan="2" className="p-3 text-right text-sm">MwSt. 19%:</td>
                    <td className="p-3 text-right">{formatPrice(order.tax_amount)}</td>
                  </tr>
                  <tr className="font-bold">
                    <td colSpan="2" className="p-3 text-right">Gesamt:</td>
                    <td className="p-3 text-right text-red-600">{formatPrice(order.total)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Customer Note */}
          {order.customer_note && (
            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
              <h3 className="font-semibold mb-1">Kundennotiz</h3>
              <p className="text-sm">{order.customer_note}</p>
            </div>
          )}

          {/* Tracking Info */}
          {order.tracking_number && (
            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
              <h3 className="font-semibold mb-1">Versandinformationen</h3>
              <p className="text-sm">
                <strong>{order.tracking_carrier}:</strong> {order.tracking_number}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="border-t pt-4 space-y-4">
            <h3 className="font-semibold">Aktionen</h3>
            
            {/* Payment Confirmation */}
            {order.payment_status === 'awaiting_payment' && (
              <Button 
                className="bg-green-600 hover:bg-green-700 w-full"
                onClick={() => onConfirmPayment(order.id)}
              >
                <CreditCard className="mr-2 h-4 w-4" />
                Zahlungseingang bestätigen
              </Button>
            )}

            {/* Status Update - Different buttons for preorders vs regular orders */}
            {order.is_preorder ? (
              <div className="flex gap-2 flex-wrap">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="bg-amber-50 border-amber-300"
                  disabled={order.order_status === 'vorbestellt'}
                  onClick={() => onUpdateStatus(order.id, 'vorbestellt')}
                >
                  Vorbestellt
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="bg-blue-50 border-blue-300"
                  disabled={order.order_status === 'reserviert'}
                  onClick={() => onUpdateStatus(order.id, 'reserviert')}
                >
                  Reserviert
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="bg-purple-50 border-purple-300"
                  disabled={order.order_status === 'lieferbereit'}
                  onClick={() => onUpdateStatus(order.id, 'lieferbereit')}
                >
                  Lieferbereit
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="bg-green-50 border-green-300"
                  disabled={order.order_status === 'abgeschlossen'}
                  onClick={() => onUpdateStatus(order.id, 'abgeschlossen')}
                >
                  Abgeschlossen
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => onUpdateStatus(order.id, 'cancelled')}
                >
                  Stornieren
                </Button>
              </div>
            ) : (
              <div className="flex gap-2 flex-wrap">
                <Button 
                  variant="outline" 
                  size="sm"
                  disabled={order.order_status === 'confirmed'}
                  onClick={() => onUpdateStatus(order.id, 'confirmed')}
                >
                  Bestätigen
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  disabled={order.order_status === 'processing'}
                  onClick={() => onUpdateStatus(order.id, 'processing')}
                >
                  In Bearbeitung
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  disabled={order.order_status === 'delivered'}
                  onClick={() => onUpdateStatus(order.id, 'delivered')}
                >
                  Zugestellt
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => onUpdateStatus(order.id, 'cancelled')}
                >
                  Stornieren
                </Button>
              </div>
            )}

            {/* Add Tracking */}
            {order.payment_status === 'paid' && !order.tracking_number && (
              <>
                {!showTrackingForm ? (
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => setShowTrackingForm(true)}
                  >
                    <Truck className="mr-2 h-4 w-4" />
                    Versand hinzufügen
                  </Button>
                ) : (
                  <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                    <select
                      value={trackingCarrier}
                      onChange={(e) => setTrackingCarrier(e.target.value)}
                      className="w-full border rounded-md px-3 py-2"
                    >
                      <option value="DHL">DHL</option>
                      <option value="UPS">UPS</option>
                      <option value="DPD">DPD</option>
                      <option value="Hermes">Hermes</option>
                      <option value="GLS">GLS</option>
                      <option value="Spedition">Spedition</option>
                    </select>
                    <Input
                      placeholder="Tracking-Nummer"
                      value={trackingNumber}
                      onChange={(e) => setTrackingNumber(e.target.value)}
                    />
                    <div className="flex gap-2">
                      <Button onClick={handleAddTracking} className="flex-1">
                        <Send className="mr-2 h-4 w-4" />
                        Versenden
                      </Button>
                      <Button variant="outline" onClick={() => setShowTrackingForm(false)}>
                        Abbrechen
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Download Invoice */}
            <a 
              href={`${API_URL}/api/orders/invoice/${order.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <Button variant="outline" className="w-full">
                <FileText className="mr-2 h-4 w-4" />
                Rechnung herunterladen
              </Button>
            </a>
            
            {/* Delete Order */}
            <Button 
              variant="outline" 
              className="w-full text-red-600 border-red-300 hover:bg-red-50"
              onClick={handleDelete}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Bestellung dauerhaft löschen
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Financing Detail Modal Component
const FinancingDetailModal = ({
  request,
  onClose,
  onUpdateStatus,
  onDeleteRequest,
  formatPrice,
  formatDate,
  getStatusBadge
}) => {
  const handleDelete = () => {
    if (onDeleteRequest) {
      onDeleteRequest(request.id);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-green-600 to-green-700 px-6 py-4 flex items-center justify-between rounded-t-lg">
          <div className="flex items-center gap-3 text-white">
            <Calculator className="h-6 w-6" />
            <div>
              <h2 className="text-lg font-bold">Finanzierungsanfrage</h2>
              <p className="text-green-100 text-sm">{formatDate(request.created_at)}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-white hover:bg-green-800 rounded-full">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Status */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Status</span>
            {getStatusBadge(request.status)}
          </div>

          {/* Product Info */}
          <div className="bg-gray-50 p-4 rounded-lg border">
            <h3 className="font-semibold text-gray-900 mb-2">Gewünschte Maschine</h3>
            <p className="text-lg font-bold text-gray-900">{request.product_name}</p>
            <div className="flex items-center justify-between mt-2">
              <span className="text-gray-600">Kaufpreis:</span>
              <span className="text-xl font-bold text-red-600">{formatPrice(request.purchase_price)}</span>
            </div>
            <div className="flex items-center justify-between mt-1">
              <span className="text-gray-600">Gewünschte Laufzeit:</span>
              <span className="font-medium">{request.term_months} Monate</span>
            </div>
          </div>

          {/* Customer Info */}
          <div className="bg-gray-50 p-4 rounded-lg border">
            <h3 className="font-semibold text-gray-900 mb-3">Kundendaten</h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-gray-400" />
                <span className="font-medium">{request.first_name} {request.last_name}</span>
              </div>
              {request.company && (
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-gray-400" />
                  <span>{request.company}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-gray-400" />
                <a href={`mailto:${request.email}`} className="text-blue-600 hover:underline">{request.email}</a>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-gray-400" />
                <a href={`tel:${request.phone}`} className="text-blue-600 hover:underline">{request.phone}</a>
              </div>
            </div>
          </div>

          {/* Message */}
          {request.message && (
            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Nachricht des Kunden
              </h3>
              <p className="text-gray-700 whitespace-pre-wrap">{request.message}</p>
            </div>
          )}

          {/* Actions */}
          <div className="border-t pt-4">
            <h3 className="font-semibold mb-3">Status ändern</h3>
            <div className="flex flex-wrap gap-2">
              <Button 
                variant="outline" 
                size="sm"
                disabled={request.status === 'neu'}
                onClick={() => onUpdateStatus(request.id, 'neu')}
                className="border-blue-200 text-blue-700 hover:bg-blue-50"
              >
                Neu
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                disabled={request.status === 'in_bearbeitung'}
                onClick={() => onUpdateStatus(request.id, 'in_bearbeitung')}
                className="border-yellow-200 text-yellow-700 hover:bg-yellow-50"
              >
                In Bearbeitung
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                disabled={request.status === 'angebot_erstellt'}
                onClick={() => onUpdateStatus(request.id, 'angebot_erstellt')}
                className="border-purple-200 text-purple-700 hover:bg-purple-50"
              >
                Angebot erstellt
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                disabled={request.status === 'abgeschlossen'}
                onClick={() => onUpdateStatus(request.id, 'abgeschlossen')}
                className="border-green-200 text-green-700 hover:bg-green-50"
              >
                Abgeschlossen
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                disabled={request.status === 'abgelehnt'}
                onClick={() => onUpdateStatus(request.id, 'abgelehnt')}
                className="border-red-200 text-red-700 hover:bg-red-50"
              >
                Abgelehnt
              </Button>
            </div>
          </div>

          {/* Contact Actions */}
          <div className="flex gap-3">
            <a href={`mailto:${request.email}`} className="flex-1">
              <Button variant="outline" className="w-full">
                <Mail className="mr-2 h-4 w-4" />
                E-Mail senden
              </Button>
            </a>
            <a href={`tel:${request.phone}`} className="flex-1">
              <Button variant="outline" className="w-full">
                <Phone className="mr-2 h-4 w-4" />
                Anrufen
              </Button>
            </a>
          </div>
          
          {/* Delete Request */}
          <Button 
            variant="outline" 
            className="w-full text-red-600 border-red-300 hover:bg-red-50"
            onClick={handleDelete}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Anfrage dauerhaft löschen
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
